import { useState } from "react";
import { BbotTable, notification } from "top-component-library";
import AddHostnameDialog from "./AddHostnameDialog";
import { CONFIG_TYPE_PRETTY_NAME, HOSTNAME_CONFIG_PAGE_SIZE_OPTIONS } from "./constants";
import { HostnameCell } from "./styles";

// Mirrors edit-hostname-configs.html's list view: a 5-column sortable table, one
// row per hostname (configured or not), red text for not-yet-configured hostnames
// and grey text for non-editable ones, an admin-only add button, and an
// admin-only bulk-delete action (legacy shows the delete affordance to anyone
// with rows selected but silently no-ops for non-admins; hidden here instead,
// since the backend doesn't enforce it either - see BBT-2268 permission-gap notes).
const HostnameConfigList = ({
  hostnameConfigs,
  isLoading,
  isAdmin,
  customerName,
  onSelectHostname,
  onAddHostname,
  onAddHostnameFromPrompt,
  onBulkDeleteHostnames,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const dataSource = hostnameConfigs.map((config) => ({ ...config, key: config.id ?? config.hostname }));

  // Every cell greys out when the row isn't editable; only the Configuration
  // Type cell also goes red for a not-yet-configured hostname.
  const cell = (content, row, notConfigured = false) => (
    <HostnameCell $notConfigured={notConfigured} $nonEditable={!row.canEdit}>
      {content}
    </HostnameCell>
  );

  const columns = [
    {
      title: "Domain Name",
      dataIndex: "hostname",
      key: "hostname",
      sorter: (a, b) => a.hostname.localeCompare(b.hostname),
      render: (hostname, row) => cell(hostname, row),
    },
    {
      title: "Aliases",
      dataIndex: "hostnameAliasesString",
      key: "aliases",
      render: (aliases, row) => cell(aliases, row),
    },
    {
      title: "Redirects to",
      dataIndex: "redirectString",
      key: "redirects_to",
      render: (redirectString, row) => cell(redirectString, row),
    },
    {
      title: "Managing Customer",
      dataIndex: "customer",
      key: "managing_customer",
      render: (customer, row) => cell(customer?.customer_name, row),
    },
    {
      title: "Configuration Type",
      dataIndex: "configType",
      key: "configuration_type",
      render: (configType, row) => cell(CONFIG_TYPE_PRETTY_NAME[configType], row, configType === "notConfigured"),
    },
  ];

  // Mirrors editSelectedConfig(): bbot.menu itself isn't a hostname you configure
  // here, it's edited from Presentation to Guests / Table/Location Codes instead.
  const onRowClick = (hostname) => {
    if (hostname === "bbot.menu") {
      notification.info({
        message:
          'Custom presentation on menus and table codes hosted by bbot.menu can be edited using the "Presentation to Guests" tab in the sidebar or using the "Table/Location Codes" tab.',
        duration: 8,
      });
      return;
    }
    onSelectHostname(hostname);
  };

  // Mirrors deleteSelectedHostnameConfigs()'s guards: never let the last domain
  // (or every domain) be removed in one go.
  const onBulkDelete = async (selectedKeys) => {
    const selectedHostnames = dataSource.filter((row) => selectedKeys.includes(row.key)).map((row) => row.hostname);

    if (hostnameConfigs.length === 1) {
      notification.info({
        message:
          hostnameConfigs[0].hostname === "bbot.menu"
            ? "You can only delete bbot.menu once you've successfully added another domain."
            : "You can't delete your last domain! Please add another domain before deleting the existing one.",
      });
      return;
    }
    if (selectedHostnames.length === hostnameConfigs.length) {
      notification.info({ message: "You can't delete all your domains! Please leave one unchecked." });
      return;
    }
    await onBulkDeleteHostnames(selectedHostnames);
  };

  return (
    <>
      <BbotTable
        id="hostname-config-list"
        title="Hostnames"
        data={dataSource}
        manuallyFormattedColumns={columns}
        loading={isLoading}
        manualOnRow={(row) => ({ onClick: () => onRowClick(row.hostname) })}
        pagination={{ pageSize: 10, pageSizeOptions: HOSTNAME_CONFIG_PAGE_SIZE_OPTIONS, showSizeChanger: true }}
        onAdd={isAdmin ? () => setShowAddDialog(true) : undefined}
        addButtonText="Add Domain Name"
        allowBulkDeletion={isAdmin}
        onBulkDelete={onBulkDelete}
      />
      <AddHostnameDialog
        visible={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        customerName={customerName}
        onAddHostname={onAddHostname}
        onAddHostnameFromPrompt={onAddHostnameFromPrompt}
      />
    </>
  );
};

export default HostnameConfigList;
